import { Button, IconButton } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { DeathData, DeathRelationship, DeathState, formatDeathRelationship } from "@/domain/deathData/deathData"
import { deathDataSchema } from "@/domain/deathData/deathDataSchemas"
import { Offering, ServiceType } from "@/domain/offerings/offering"
import { DocType } from "@/domain/shared"
import { calculateAgeStr } from "@/utils/dates"
import { formatDocNumber } from "@/utils/helpers"
import { zodResolver } from "@hookform/resolvers/zod"
import { Clock } from "lucide-react"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import z from "zod"
import DatePicker from "../../shared/DatePicker"
import DateSelector from "../../shared/DateSelector"
import { OptionalLabel } from "../../shared/utils"
import { formatDate } from "date-fns"

interface DeathDataFormProps {
  offering?: Offering
  onSubmit: (data: DeathData[]) => void
  onBack: () => void
  existingData: DeathData[]
}

export const DeathDataForm = ({ offering, onSubmit, onBack, existingData }: DeathDataFormProps) => {
  const [peopleList, setPeopleList] = useState<DeathData[]>(existingData || [])
  const [error, setError] = useState<string>('')
  const serviceType = offering?.serviceType
  const deathDataSchemaForService = deathDataSchema(serviceType || '');
  type DeathDataFormValues = z.infer<typeof deathDataSchemaForService>

  const MAX_PEOPLE = 4
  const maxPeopleReached = peopleList.length === MAX_PEOPLE

  const defaultFormValues = {
    name: '',
    docType: undefined,
    docNumber: '',
    birthDate: undefined,
    deathDate: undefined,
    state: DeathState.CREMATED,
    cremationDate: undefined,
    intermentDate: undefined,
    disintermentDate: undefined,
    reductionDate: undefined,
    relationshipWithDeceased: undefined,
    thumbLevel: undefined,
    entryNumber: '',
    volume: '',
    year: '',
    leaf: '',
    office: '',
    mortuary: '',
    medicOfDeceased: '',
    deathCertificateAuthorization: ''
  }

  const deathDataForm = useForm<DeathDataFormValues>({
    resolver: zodResolver(deathDataSchemaForService),
    defaultValues: defaultFormValues
  })

  // console.log(deathDataForm.formState.errors)

  useEffect(() => {
    if (existingData.length > 0) {
      const data = existingData[0]
      deathDataForm.reset({
        name: data.name,
        docType: data.docType as DocType || undefined,
        docNumber: data.docNumber,
        birthDate: data.birthDate,
        deathDate: data.deathDate,
        relationshipWithDeceased: data.relationshipWithDeceased || undefined,
        entryNumber: data.entryNumber || '',
        volume: data.volume || '',
        year: data.year,
        leaf: data.leaf || '',
        office: data.office || '',
        mortuary: data.mortuary || '',
        medicOfDeceased: data.medicOfDeceased || '',
        deathCertificateAuthorization: data.deathCertificateAuthorization || ''
      })
    }
  }, [existingData])

  const birthDate = deathDataForm.watch('birthDate')
  const deathDate = deathDataForm.watch('deathDate')
  const intermentDate = deathDataForm.watch('intermentDate')

  const handleDocNumberChange = (value: string) => {
    const docType = deathDataForm.getValues("docType") as DocType || undefined
    deathDataForm.setValue('docNumber', formatDocNumber(value, docType))
  }

  const validateAndAddPerson = () => {
    setError('')

    deathDataForm.trigger().then((isValid) => {
      if (!isValid) {
        setError('Por favor complete todos los campos requeridos correctamente')
        return
      }

      const formData = deathDataForm.getValues()

      const newPerson: DeathData = {
        ...formData as DeathData,
        state: DeathState.CREMATED
      }

      const updatedList = [...peopleList, newPerson]

      setPeopleList(updatedList)
      deathDataForm.reset(defaultFormValues)
    })
  }

  const removePerson = (index: number) => {
    setPeopleList(peopleList.filter((_, i) => i !== index))
    deathDataForm.reset(defaultFormValues)
    setError('')
  }

  const editPerson = (index: number) => {
    const person = peopleList[index]
    deathDataForm.reset({
      name: person.name,
      docType: person.docType,
      docNumber: person.docNumber,
      birthDate: person.birthDate,
      deathDate: person.deathDate,
      state: person.state,
      cremationDate: person.cremationDate,
      intermentDate: person.intermentDate,
      disintermentDate: person.disintermentDate,
      reductionDate: person.reductionDate,
      relationshipWithDeceased: person.relationshipWithDeceased || undefined,
      thumbLevel: person.thumbLevel,
      entryNumber: person.entryNumber || '',
      volume: person.volume || '',
      year: person.year,
      leaf: person.leaf || '',
      office: person.office || '',
      mortuary: person.mortuary || '',
      medicOfDeceased: person.medicOfDeceased || '',
      deathCertificateAuthorization: person.deathCertificateAuthorization || ''
    })
    setPeopleList(peopleList.filter((_, i) => i !== index))
    setError('')
  }

  const handleReset = () => {
    deathDataForm.reset(defaultFormValues)
    setError('')
  }

  const handleDeathDataFormSubmit = () => {
    if (peopleList.length > 0) {
      onSubmit(peopleList)
      return
    }

    deathDataForm.trigger().then((isValid) => {
      if (!isValid) {
        setError('Por favor complete todos los campos requeridos correctamente o agregue una persona a la lista')
        return
      }
      onSubmit([{
        ...deathDataForm.getValues() as DeathData,
        state: DeathState.CREMATED
      }])
    })
  }

  const nextButtonDisabled = deathDataForm.formState.isSubmitting
  const canAddMorePeople = !maxPeopleReached

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <h3 className="text-lg font-medium flex flex-col">
          <span>{offering!.name}</span>
          <span className="text-sm font-mono text-muted-foreground">{" "}(Código {offering!.code})</span>
        </h3>
      </div>

      {canAddMorePeople &&
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">
              Datos del Fallecido
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium">Apellido, Nombre</label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-md ${deathDataForm.formState.errors.name ? 'border-red-500' : ''}`}
                  value={deathDataForm.watch('name') || ''}
                  onChange={(e) => deathDataForm.setValue('name', e.target.value)}
                />
                {deathDataForm.formState.errors.name && (
                  <p className="text-red-500 text-sm mt-1">
                    {deathDataForm.formState.errors.name.message}
                  </p>
                )}
              </div>


              <div className="space-y-2">
                <label htmlFor="relationshipWithDeceased" className="text-sm font-medium">Parentesco del titular con el fallecido</label>
                <OptionalLabel />
                <Select
                  onValueChange={(value) => deathDataForm.setValue('relationshipWithDeceased', value as DeathRelationship)}
                  value={deathDataForm.watch('relationshipWithDeceased') || ''}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar parentesco" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(DeathRelationship).map((rel) => (
                      <SelectItem key={rel} value={rel}>
                        {formatDeathRelationship(rel)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {deathDataForm.formState.errors.relationshipWithDeceased && (
                  <p className="text-red-500 text-sm mt-1">
                    {deathDataForm.formState.errors.relationshipWithDeceased.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="docType" className="text-sm font-medium">Tipo de documento</label>
                {serviceType === ServiceType.CREMACIONES && <OptionalLabel />}
                <Select
                  onValueChange={(value) => deathDataForm.setValue('docType', value as DocType)}
                  value={deathDataForm.watch('docType') || ''}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo de documento" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(DocType).map((docType) => (
                      <SelectItem key={docType} value={docType}>
                        {docType}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {deathDataForm.formState.errors.docType && (
                  <p className="text-red-500 text-sm mt-1">
                    {deathDataForm.formState.errors.docType.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="docNumber" className="text-sm font-medium">Número de documento</label>
                {serviceType === ServiceType.CREMACIONES && <OptionalLabel />}
                <input
                  type="text"
                  placeholder="12.345.678"
                  className={`w-full p-2 border rounded-md ${deathDataForm.formState.errors.docNumber ? 'border-red-500' : ''}`}
                  value={deathDataForm.watch('docNumber') || ''}
                  onChange={(e) => handleDocNumberChange(e.target.value)}
                />
                {deathDataForm.formState.errors.docNumber && (
                  <p className="text-red-500 text-sm mt-1">
                    {deathDataForm.formState.errors.docNumber.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="birthDate" className="text-sm font-medium">Fecha de nacimiento</label>
                {serviceType === ServiceType.CREMACIONES && <OptionalLabel />}
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <DateSelector
                      key={`birth-${birthDate?.getTime() || 'empty'}`}
                      initialDate={birthDate || undefined}
                      onDateSelected={(date: Date) => {
                        deathDataForm.setValue('birthDate', date)
                      }}
                    />
                  </div>
                  {birthDate && (
                    <span className="text-md font-mono text-gray-600 whitespace-nowrap">
                      ({serviceType === ServiceType.CREMACIONES && deathDate ? calculateAgeStr(birthDate, deathDate) : calculateAgeStr(birthDate)})
                    </span>
                  )}
                </div>
                {deathDataForm.formState.errors.birthDate && (
                  <p className="text-red-500 text-sm mt-1">
                    {deathDataForm.formState.errors.birthDate.message}
                  </p>
                )}
              </div>

              {serviceType === ServiceType.CREMACIONES && (
                <div className="space-y-2">
                  <label htmlFor="deathDate" className="text-sm font-medium">Fecha de defunción</label>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <DateSelector
                        key={`death-${deathDate?.getTime() || 'empty'}`}
                        initialDate={deathDate || undefined}
                        onDateSelected={(date: Date) => {
                          deathDataForm.setValue('deathDate', date)
                        }}
                      />
                    </div>
                    <IconButton variant="default" title="Fecha actual"
                      onClick={() => deathDataForm.setValue('deathDate', new Date())}>
                      <Clock className="size-4 mr-1" />
                      Elegir fecha actual
                    </IconButton>
                  </div>
                  {deathDataForm.formState.errors.deathDate && (
                    <p className="text-red-500 text-sm mt-1">
                      {deathDataForm.formState.errors.deathDate.message}
                    </p>
                  )}
                </div>
              )}

              {serviceType === ServiceType.PARCELAS && (
                <>
                  <div className="space-y-2">
                    <label htmlFor="deathDate" className="text-sm font-medium">Fecha de fallecimiento</label>
                    <DatePicker
                      key={`death-parcel-${deathDate?.toString() || 'empty'}`}
                      date={deathDate as Date}
                      onSelect={(date: Date) => { deathDataForm.setValue('deathDate', date) }} />
                    {deathDataForm.formState.errors.deathDate && (
                      <p className="text-red-500 text-sm mt-1">
                        {deathDataForm.formState.errors.deathDate.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="intermentDate" className="text-sm font-medium">Fecha de inhumación (opcional)</label>
                    <DatePicker
                      key={`interment-${intermentDate?.toString() || 'empty'}`}
                      date={intermentDate as Date}
                      onSelect={(date: Date) => { deathDataForm.setValue('intermentDate', date) }} />
                    {deathDataForm.formState.errors.intermentDate && (
                      <p className="text-red-500 text-sm mt-1">
                        {deathDataForm.formState.errors.intermentDate.message}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold mb-3">Datos de Registro <OptionalLabel /></h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label htmlFor="entryNumber" className="text-xs font-medium">Acta Nro</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md text-sm"
                    value={deathDataForm.watch('entryNumber') || ''}
                    onChange={(e) => deathDataForm.setValue('entryNumber', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="volume" className="text-xs font-medium">Tomo</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md text-sm"
                    value={deathDataForm.watch('volume') || ''}
                    onChange={(e) => deathDataForm.setValue('volume', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="year" className="text-xs font-medium">Año</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-md text-sm"
                    value={deathDataForm.watch('year') || ''}
                    onChange={(e) => deathDataForm.setValue('year', e.target.value ? parseInt(e.target.value)?.toString() : '')}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="leaf" className="text-xs font-medium">Folio</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md text-sm"
                    value={deathDataForm.watch('leaf') || ''}
                    onChange={(e) => deathDataForm.setValue('leaf', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="office" className="text-xs font-medium">Oficina/Delegación</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md text-sm"
                    value={deathDataForm.watch('office') || ''}
                    onChange={(e) => deathDataForm.setValue('office', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="mortuary" className="text-xs font-medium">Funeraria</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md text-sm"
                    value={deathDataForm.watch('mortuary') || ''}
                    onChange={(e) => deathDataForm.setValue('mortuary', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="medicOfDeceased" className="text-xs font-medium">Médico</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md text-sm"
                    value={deathDataForm.watch('medicOfDeceased') || ''}
                    onChange={(e) => deathDataForm.setValue('medicOfDeceased', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="deathCertificateAuthorization" className="text-xs font-medium">Autorización de Sepultura</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md text-sm"
                    value={deathDataForm.watch('deathCertificateAuthorization') || ''}
                    onChange={(e) => deathDataForm.setValue('deathCertificateAuthorization', e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={validateAndAddPerson}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  Agregar Persona ({peopleList.length}/{MAX_PEOPLE})
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {peopleList.length > 0 && (
        <div className="space-y-3">
          {peopleList.length > 0 && <h3 className="text-lg font-medium">
            {peopleList.length > 1 ? 'Personas agregadas' : 'Persona agregada'}
            </h3>}
          {peopleList.map((person, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg border">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <p className="font-medium">{person.name}</p>
                  <p className="text-sm text-gray-600">
                    {person.docType} {person.docNumber}
                  </p>
                  <p className="text-sm text-gray-600">
                    {!person.birthDate && person.deathDate && `Fallecimiento: ${formatDate(person.deathDate, 'd/MM/yyyy')}`}
                    {person.birthDate && person.deathDate && `Nacimiendo: ${formatDate(person.birthDate, 'd/MM/yyyy')}. Fallecimiento: ${formatDate(person.deathDate, 'd/MM/yyyy')} (${calculateAgeStr(new Date(person.birthDate), new Date(person.deathDate))})`}
                  </p>
                  {person.relationshipWithDeceased && (
                    <p className="text-sm text-gray-500 italic">
                      Parentesco del titular con el fallecido: {formatDeathRelationship(person.relationshipWithDeceased)}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editPerson(index)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Editar
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removePerson(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Eliminar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Atrás
        </Button>
        <div className="space-x-2">
          <Button type="button" onClick={handleDeathDataFormSubmit} disabled={nextButtonDisabled}>
            Continuar
          </Button>
        </div>
      </div>
    </div>
  )
}